// api/auth.api.js - UPDATED
import API_BASE from "./api";

export class AuthService {
    // Helper to get cookies
    static getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    // Helper to set cookie
    static setCookie(name, value, days = 7) {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
    }

    // Helper to delete cookie
    static deleteCookie(name) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }

    // Register
    static async register(userData) {
        const response = await fetch(`${API_BASE}/auth/register`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(userData),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Registration failed');
        }

        // Store user data
        this.storeUserData(data.user, data.accessToken, data.refreshToken);

        return data;
    }

    // Login
    static async login(email, password, rememberMe = false) {
        const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({email, password, rememberMe}),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Login failed');
        }

        // Store user data
        this.storeUserData(data.user, data.accessToken, data.refreshToken, rememberMe);

        return data;
    }

    // Store user data
    static storeUserData(user, accessToken = null, refreshToken = null, rememberMe = false) {
        // Always store in sessionStorage for current session
        sessionStorage.setItem('kromaic_user', JSON.stringify(user));

        if (rememberMe) {
            // Store in localStorage for persistence
            localStorage.setItem('kromaic_user', JSON.stringify(user));
        } else {
            // Remove from localStorage if exists
            localStorage.removeItem('kromaic_user');
        }

        // Store tokens if provided
        if (accessToken) {
            sessionStorage.setItem('kromaic_access_token', accessToken);
            if (rememberMe) {
                localStorage.setItem('kromaic_access_token', accessToken);
            }
        }

        if (refreshToken) {
            sessionStorage.setItem('kromaic_refresh_token', refreshToken);
            if (rememberMe) {
                localStorage.setItem('kromaic_refresh_token', refreshToken);
            }
        }

        // Set last active timestamp
        const now = new Date().toISOString();
        sessionStorage.setItem('kromaic_last_active', now);
        if (rememberMe) {
            localStorage.setItem('kromaic_last_active', now);
        }
    }

    // Clear user data
    static clearUserData() {
        // Clear session storage
        sessionStorage.removeItem('kromaic_user');
        sessionStorage.removeItem('kromaic_access_token');
        sessionStorage.removeItem('kromaic_refresh_token');
        sessionStorage.removeItem('kromaic_last_active');
        sessionStorage.removeItem('kromaic_redirect_after_login');

        // Clear local storage
        localStorage.removeItem('kromaic_user');
        localStorage.removeItem('kromaic_access_token');
        localStorage.removeItem('kromaic_refresh_token');
        localStorage.removeItem('kromaic_last_active');
        localStorage.removeItem('kromaic_redirect_after_login');

        // Clear specific cookies
        this.deleteCookie('accessToken');
        this.deleteCookie('refreshToken');
        this.deleteCookie('token');
        this.deleteCookie('auth_token');
        this.deleteCookie('session');
    }

    // Get current user from storage
    static getStoredUser() {
        // Try session storage first
        let user = sessionStorage.getItem('kromaic_user');

        if (!user) {
            // Try local storage
            user = localStorage.getItem('kromaic_user');
        }

        return user ? JSON.parse(user) : null;
    }

    // Get stored token
    static getStoredToken() {
        // Try session storage first
        let token = sessionStorage.getItem('kromaic_access_token');

        if (!token) {
            // Try local storage
            token = localStorage.getItem('kromaic_access_token');
        }

        return token;
    }

    // Check if token is expired
    static isTokenExpired() {
        const lastActive = sessionStorage.getItem('kromaic_last_active') ||
            localStorage.getItem('kromaic_last_active');

        if (!lastActive) return true;

        const lastActiveTime = new Date(lastActive).getTime();
        const currentTime = new Date().getTime();
        const sessionTimeout = 15 * 60 * 1000; // 15 minutes

        return (currentTime - lastActiveTime) > sessionTimeout;
    }

    // Update last active timestamp
    static updateLastActive() {
        const now = new Date().toISOString();
        sessionStorage.setItem('kromaic_last_active', now);

        // Also update localStorage if user chose remember me
        if (localStorage.getItem('kromaic_user')) {
            localStorage.setItem('kromaic_last_active', now);
        }
    }

    // Check authentication
    static async checkAuth() {
        try {
            // Check if we have stored user data
            const storedUser = this.getStoredUser();
            const storedToken = this.getStoredToken();

            if (!storedUser || !storedToken) {
                return { authenticated: false };
            }

            // Check if token is expired
            if (this.isTokenExpired()) {
                // Try to refresh token
                try {
                    await this.refreshToken();
                } catch (error) {
                    // Refresh failed, clear data
                    this.clearUserData();
                    return { authenticated: false };
                }
            }

            // Update last active timestamp
            this.updateLastActive();

            // Make API call to verify with server
            const response = await fetch(`${API_BASE}/auth/check`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${storedToken}`
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                return {
                    authenticated: true,
                    user: storedUser,
                    ...data
                };
            } else {
                // Server says not authenticated
                this.clearUserData();
                return { authenticated: false };
            }

        } catch (error) {
            console.error('Auth check error:', error);
            return { authenticated: false };
        }
    }

    // Refresh token
    static async refreshToken() {
        try {
            const refreshToken = sessionStorage.getItem('kromaic_refresh_token') ||
                localStorage.getItem('kromaic_refresh_token');

            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await fetch(`${API_BASE}/auth/refresh-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refreshToken }),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Token refresh failed');
            }

            const data = await response.json();

            // Update tokens in storage
            if (data.accessToken) {
                sessionStorage.setItem('kromaic_access_token', data.accessToken);
                if (localStorage.getItem('kromaic_user')) {
                    localStorage.setItem('kromaic_access_token', data.accessToken);
                }
            }

            this.updateLastActive();

            return data;
        } catch (error) {
            // Refresh failed, clear all data
            this.clearUserData();
            throw error;
        }
    }

    // Logout
    static async logout() {
        try {
            // Call server logout
            await fetch(`${API_BASE}/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error('Server logout failed:', error);
        } finally {
            // Always clear local data
            this.clearUserData();

            // Redirect to home page
            window.location.href = '/';
        }
    }

    // Forgot Password
    static async forgotPassword(email) {
        const response = await fetch(`${API_BASE}/auth/forgot-password`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({email})
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to send reset email');
        }

        return data;
    }

    // Reset Password
    static async resetPassword(token, newPassword) {
        const response = await fetch(`${API_BASE}/auth/reset-password/${token}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({password: newPassword, confirmPassword: newPassword}),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to reset password');
        }

        return data;
    }

    // Get current user from server
    static async getCurrentUser() {
        const token = this.getStoredToken();

        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(`${API_BASE}/auth/me`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            credentials: 'include'
        });

        const data = await response.json();
        // console.log(data)
        if (!response.ok) {
            throw new Error(data.message || 'Failed to get user data');
        }

        // Update stored user data
        if (data.data) {
            this.storeUserData(data.data);
        }

        return data.data;
    }

    // Setup automatic token refresh
    static setupAutoRefresh() {
        // Refresh token every 14 minutes (before 15 min expiry)
        setInterval(async () => {
            const user = this.getStoredUser();
            if (user && this.isTokenExpired()) {
                try {
                    await this.refreshToken();
                    console.log('Token auto-refreshed successfully');
                } catch (error) {
                    console.error('Auto-refresh failed:', error);
                }
            }
        }, 14 * 60 * 1000); // 14 minutes
    }
}